import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import './BlogList.scss';
import Seo from '../../Seo/Seo';
import blogHeroImage from '../../../Assets/blogs/blog-hero.webp';
import {
  POSTS_PER_PAGE,
  blogListSchema,
  blogPosts,
  getBlogPagePath,
  getExcerpt,
  getPostPath,
  getReadTime,
} from '../blogUtils';

const BlogList = () => {
  const { pageNumber } = useParams();
  const currentPage = pageNumber ? Number(pageNumber) : 1;
  const totalPages = Math.ceil(blogPosts.length / POSTS_PER_PAGE);
  const hasPagination = totalPages > 1;
  const isInvalidPage =
    !Number.isInteger(currentPage) ||
    currentPage < 1 ||
    (totalPages > 0 && currentPage > totalPages) ||
    (pageNumber && currentPage === 1);

  if (isInvalidPage) {
    return <Navigate to="/blog" replace />;
  }

  const pagePosts = blogPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );
  const seoTitle =
    currentPage === 1
      ? 'Klean Leaf Blog | Laundry Detergent Sheets & Private Label Manufacturing'
      : `Klean Leaf Blog - Page ${currentPage} | Laundry Detergent Sheets & Manufacturing`;
  const seoDescription =
    currentPage === 1
      ? 'Explore Klean Leaf insights on laundry detergent sheets, private label manufacturing, formula development, packaging options, samples, MOQ, and product launch support.'
      : `Explore page ${currentPage} of Klean Leaf insights on laundry detergent sheets, private label manufacturing, formula development, packaging, samples, MOQ, and launch support.`;

  return (
    <div className="blog-list page-top-settings primary">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={getBlogPagePath(currentPage)}
        prevPath={currentPage > 1 ? getBlogPagePath(currentPage - 1) : undefined}
        nextPath={currentPage < totalPages ? getBlogPagePath(currentPage + 1) : undefined}
        schema={blogListSchema(pagePosts, currentPage)}
      />
      <header className="blog-list-header">
        <div className="blog-hero-image-wrap">
          <img
            src={blogHeroImage}
            alt="Klean Leaf laundry detergent sheet manufacturing and production workflow"
            className="blog-hero-image"
            decoding="async"
            fetchpriority="high"
          />
        </div>
        <div className="blog-hero-content">
          <span className="blog-eyebrow">Klean Leaf Blog</span>
          <h1>Laundry Detergent Sheets &amp; Private Label Manufacturing Blog</h1>
          <p className="blog-page-intro">
            Practical insights on laundry detergent sheets, formula development, packaging options,
            MOQ, sampling, production workflow, and private label product launch support.
          </p>
          <div className="blog-hero-actions">
            <a href="#latest-articles-heading" className="blog-hero-primary">Read Latest Articles</a>
            <Link to="/products" className="blog-hero-secondary">Explore Product Options</Link>
          </div>
        </div>
      </header>

      <section className="blog-latest-header" aria-labelledby="latest-articles-heading">
        <div>
          <span>{blogPosts.length} manufacturing insights</span>
          <h2 id="latest-articles-heading">Latest Articles</h2>
        </div>
      </section>

      <div className="blog-cards">
        {pagePosts.map((post, index) => (
          <Link
            key={post.id}
            className="blog-card"
            to={getPostPath(post)}
            aria-label={`Read article: ${post.title}`}
          >
            <div className="blog-image-wrap">
              <img
                src={post.imageUrl1}
                alt={post.title}
                className="blog-image"
                loading={index < 3 ? undefined : 'lazy'}
                decoding="async"
              />
            </div>
            <div className="blog-details">
              <span className="blog-meta">{post.date} · {getReadTime(post)}</span>
              <h3 className="blog-title">{post.title}</h3>
              <p className="blog-summary-text">{getExcerpt(post)}</p>
              <div className="blog-card-footer">
                <span className="blog-read-more">Read more</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {hasPagination && (
        <nav className="blog-pagination" aria-label="Blog pagination">
          {currentPage > 1 ? (
            <Link className="pagination-control" to={getBlogPagePath(currentPage - 1)}>
              Previous
            </Link>
          ) : (
            <span className="pagination-control disabled" aria-disabled="true">
              Previous
            </span>
          )}

          {Array.from({ length: totalPages }, (_, index) => {
            const page = index + 1;
            const isCurrent = page === currentPage;

            return isCurrent ? (
              <span
                key={page}
                className="pagination-page active"
                aria-current="page"
              >
                {page}
              </span>
            ) : (
              <Link key={page} className="pagination-page" to={getBlogPagePath(page)}>
                {page}
              </Link>
            );
          })}

          {currentPage < totalPages ? (
            <Link className="pagination-control" to={getBlogPagePath(currentPage + 1)}>
              Next
            </Link>
          ) : (
            <span className="pagination-control disabled" aria-disabled="true">
              Next
            </span>
          )}
        </nav>
      )}

      <section className="blog-cta" aria-labelledby="blog-cta-heading">
        <div>
          <span>Private label support</span>
          <h2 id="blog-cta-heading">Find the product format for your brand</h2>
          <p>
            Put these guides into practice. Compare Klean Leaf’s laundry and home-care
            products, then discuss formula, packaging, and sample requirements with our team.
          </p>
        </div>
        <div className="blog-cta-actions">
          <Link to="/products" className="blog-cta-primary">Browse Product Formats</Link>
          <Link to="/contact" className="blog-cta-secondary">Discuss Your Project</Link>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
